import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowUpRight,
  Globe,
  ArrowSquareOut,
  X,
  Cursor,
  CaretLeft,
  CaretRight,
  ShareNetwork,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { SectionHeader } from "@/components/site/Services";

async function shareSite(project) {
  const data = { title: project.name, text: `${project.name} — a live site built by WebJesters`, url: project.liveUrl };
  try {
    if (navigator.share) {
      await navigator.share(data);
      return;
    }
    await navigator.clipboard.writeText(project.liveUrl);
    toast.success(`Link copied: ${project.domain}`);
  } catch (err) {
    if (err?.name !== "AbortError") toast.error(`Couldn't share. Link: ${project.liveUrl}`);
  }
}

const DEMO_WASH_URL =
  process.env.REACT_APP_DEMO_WASH_URL ||
  "https://powerwash.webjesters.com/";
const CLIENT_CABRESETAZ_URL =
  process.env.REACT_APP_CLIENT_CABRESETAZ_URL || "https://cabresetaz.com/";
const CLIENT_SCRAPMANS_URL =
  process.env.REACT_APP_CLIENT_SCRAPMANS_URL || "https://scrapmanmetals.com/";

const projects = [
  {
    id: "interior-reset",
    name: "Cab Reset AZ",
    category: "Mobile Auto Detailing",
    domain: "cabresetaz.com",
    liveUrl: CLIENT_CABRESETAZ_URL,
    accent: "#facc15",
    image: "/portfolio/interior-reset-mobile.jpg",
    status: "Live · Owner's business",
    featured: true,
    need: "A mobile detailer whose customers want to request a quote from their phone without calling mid-day.",
    delivered: "Mobile-first site with a quote form, click-to-call, and clear service positioning — hosted and managed by WebJesters.",
  },
  {
    id: "scrapmans-metals",
    name: "Scrapman's Metals",
    category: "Scrap Metal Recycling",
    domain: "scrapmanmetals.com",
    liveUrl: CLIENT_SCRAPMANS_URL,
    accent: "#ef4444",
    image: "/portfolio/scrapmans-metals-mobile.jpg",
    status: "Live · Client site",
    need: "A family-owned scrap yard fielding the same questions all day: what do you take, and can I bring a bulk load?",
    delivered: "Call-first layout, a full materials showcase, and a bulk-load quote form with photo uploads.",
  },
  {
    id: "sonoran-clean",
    name: "Sonoran Clean",
    category: "Pressure Washing",
    domain: "powerwash.webjesters.com",
    liveUrl: DEMO_WASH_URL,
    accent: "#38bdf8",
    image: "/portfolio/sonoran-clean-mobile.jpg",
    status: "Demo build",
    need: "A demo we built to show what a pressure washing company gets with the standard build.",
    delivered: "Quote forms, local SEO structure, click-to-call, and managed hosting — the same build our clients receive.",
  },
];

const pad = (n) => String(n).padStart(2, "0");

export default function Portfolio() {
  const [livePreview, setLivePreview] = useState(null);
  const trackRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const scrollToIndex = useCallback((idx) => {
    const track = trackRef.current;
    if (!track) return;
    const target = track.querySelector(`[data-card-index="${idx}"]`);
    if (target) {
      const offsetLeft = target.offsetLeft;
      const cardWidth = target.offsetWidth;
      const trackWidth = track.clientWidth;
      track.scrollTo({
        left: offsetLeft - (trackWidth - cardWidth) / 2,
        behavior: "smooth",
      });
    }
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    let raf = null;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        const cards = Array.from(track.querySelectorAll("[data-card-index]"));
        const trackRect = track.getBoundingClientRect();
        const trackCenter = trackRect.left + trackRect.width / 2;
        let closest = 0;
        let closestDist = Infinity;
        cards.forEach((c, i) => {
          const r = c.getBoundingClientRect();
          const center = r.left + r.width / 2;
          const dist = Math.abs(center - trackCenter);
          if (dist < closestDist) {
            closestDist = dist;
            closest = i;
          }
        });
        setActiveIndex(closest);
        raf = null;
      });
    };
    track.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => {
      track.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  const goPrev = () => scrollToIndex(Math.max(0, activeIndex - 1));
  const goNext = () =>
    scrollToIndex(Math.min(projects.length - 1, activeIndex + 1));

  return (
    <section
      id="work"
      data-testid="portfolio-section"
      className="relative pt-14 pb-14 sm:py-28 md:py-36 bg-[#0c0c0d] border-y border-white/[0.06] overflow-hidden"
    >
      {/* soft ambient backdrop, very faint */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-[0.18]"
        style={{
          background:
            "radial-gradient(60% 50% at 50% 0%, rgba(255,255,255,0.04), transparent 60%)",
        }}
      />

      <div className="relative max-w-[1500px] mx-auto px-5 md:px-10 lg:px-14">
        {/* Header row */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <SectionHeader
            eyebrow="Selected Work"
            title="Real businesses, live on WebJesters."
            description="Two live business sites and one demo build, all hosted and managed by us. Tap any of them to load the live site. Shown with each client's permission; names and logos belong to their owners."
          />

          {/* Editorial counter — desktop */}
          <div className="hidden md:flex items-baseline gap-1 font-display text-zinc-500">
            <span
              data-testid="portfolio-counter-current"
              className="text-white text-3xl tracking-tight tabular-nums transition-all duration-300"
            >
              {pad(activeIndex + 1)}
            </span>
            <span className="text-zinc-600 text-lg">/</span>
            <span className="text-zinc-500 text-lg tabular-nums">
              {pad(projects.length)}
            </span>
          </div>

          {/* Mobile-only counter + swipe affordance — placed in the header so it's
              visible BEFORE the user scrolls into card content */}
          <div className="md:hidden flex items-baseline justify-between gap-4 -mt-2">
            <div className="flex items-baseline gap-1 font-display">
              <span
                data-testid="portfolio-counter-current-mobile-top"
                className="text-white text-2xl tracking-tight tabular-nums"
              >
                {pad(activeIndex + 1)}
              </span>
              <span className="text-zinc-600 text-base">/</span>
              <span className="text-zinc-500 text-base tabular-nums">
                {pad(projects.length)}
              </span>
            </div>
            <AnimatePresence>
              {activeIndex < projects.length - 1 && (
                <motion.span
                  key="swipe-hint-top"
                  initial={{ opacity: 0, x: -4 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 4 }}
                  transition={{ duration: 0.35 }}
                  data-testid="portfolio-swipe-hint-top"
                  className="text-[10.5px] tracking-[0.22em] uppercase text-zinc-400 inline-flex items-center gap-1.5"
                >
                  Swipe
                  <motion.span
                    aria-hidden
                    animate={{ x: [0, 4, 0] }}
                    transition={{
                      duration: 1.6,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                    className="inline-flex"
                  >
                    <CaretRight size={11} weight="bold" />
                  </motion.span>
                </motion.span>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Carousel zone — hover-revealed arrows live here */}
        <div className="group/showcase relative mt-8 sm:mt-14 md:mt-16">
          {/* Subtle hover-revealed nav — desktop only */}
          <button
            type="button"
            onClick={goPrev}
            disabled={activeIndex === 0}
            data-testid="portfolio-prev"
            aria-label="Previous site"
            className="
              hidden md:inline-flex
              absolute left-2 lg:left-4 top-1/2 -translate-y-1/2 z-20
              items-center justify-center w-11 h-11 rounded-full
              border border-white/[0.08] bg-black/40 backdrop-blur-md
              text-white/80 hover:text-white hover:border-white/[0.2]
              transition-all duration-300
              opacity-0 group-hover/showcase:opacity-100
              disabled:opacity-0 disabled:cursor-default
            "
          >
            <CaretLeft size={15} weight="bold" />
          </button>
          <button
            type="button"
            onClick={goNext}
            disabled={activeIndex === projects.length - 1}
            data-testid="portfolio-next"
            aria-label="Next site"
            className="
              hidden md:inline-flex
              absolute right-2 lg:right-4 top-1/2 -translate-y-1/2 z-20
              items-center justify-center w-11 h-11 rounded-full
              border border-white/[0.08] bg-black/40 backdrop-blur-md
              text-white/80 hover:text-white hover:border-white/[0.2]
              transition-all duration-300
              opacity-0 group-hover/showcase:opacity-100
              disabled:opacity-0 disabled:cursor-default
            "
          >
            <CaretRight size={15} weight="bold" />
          </button>

          {/* Scroll track */}
          <div
            ref={trackRef}
            data-testid="portfolio-carousel-track"
            className="
              -mx-5 md:-mx-10 lg:-mx-14
              px-[12vw] sm:px-10 md:px-10 lg:px-14
              flex gap-4 sm:gap-7 md:gap-9
              overflow-x-auto snap-x snap-proximity
              no-scrollbar
            "
            style={{
              overscrollBehaviorX: "contain",
            }}
          >
            {projects.map((p, i) => (
              <ProjectCard
                key={p.id}
                project={p}
                index={i}
                isActive={i === activeIndex}
                onPreview={() => setLivePreview(p)}
              />
            ))}
            <div aria-hidden className="flex-shrink-0 w-1 md:w-[8%]" />
          </div>
        </div>

        {/* Editorial name strip below the carousel */}
        <div className="mt-7 sm:mt-12">
          {/* Clickable demo-name strip */}
          <nav
            data-testid="portfolio-name-nav"
            className="flex flex-wrap items-center gap-x-7 gap-y-3 sm:gap-x-10"
          >
            {projects.map((p, i) => {
              const active = i === activeIndex;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => scrollToIndex(i)}
                  data-testid={`portfolio-name-${p.id}`}
                  className="group/name flex items-center gap-2.5 text-left"
                >
                  <span
                    className={`font-display text-[12px] tabular-nums tracking-[0.15em] transition-colors duration-300 ${
                      active ? "text-white" : "text-zinc-600"
                    }`}
                  >
                    {pad(i + 1)}
                  </span>
                  <span
                    className={`text-[13.5px] sm:text-sm transition-colors duration-300 ${
                      active
                        ? "text-white"
                        : "text-zinc-500 group-hover/name:text-zinc-300"
                    }`}
                  >
                    {p.name}
                  </span>
                  <span
                    aria-hidden
                    className={`block h-px transition-all duration-500 ${
                      active ? "w-10 bg-white" : "w-0 bg-transparent"
                    }`}
                  />
                </button>
              );
            })}
          </nav>
        </div>

        {/* Quiet "currently building" line — editorial, no card */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="mt-10 sm:mt-16 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-6 sm:pt-8 border-t border-white/[0.05]"
        >
          <div className="text-[13.5px] text-zinc-400">
            More local business projects currently in progress.
          </div>
          <a
            href="#contact"
            className="group/cta inline-flex items-center gap-2 text-[13.5px] text-zinc-300 hover:text-white transition-colors"
          >
            Request a build like this
            <ArrowUpRight
              size={13}
              weight="bold"
              className="transition-transform group-hover/cta:translate-x-0.5 group-hover/cta:-translate-y-0.5"
            />
          </a>
        </motion.div>
      </div>

      <LivePreviewModal
        project={livePreview}
        onClose={() => setLivePreview(null)}
      />
    </section>
  );
}

function ProjectCard({ project, index, isActive, onPreview }) {
  const [hover, setHover] = useState(false);

  return (
    <motion.div
      data-card-index={index}
      data-testid={`portfolio-item-${project.id}`}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, delay: index * 0.08, ease: [0.22, 1, 0.36, 1] }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className={`
        snap-center flex-shrink-0
        w-[76vw] sm:w-[80%] md:w-[72%] lg:w-[64%] xl:w-[58%]
        relative rounded-[28px] sm:rounded-[32px]
        border border-white/[0.08]
        bg-[#202028]
        px-6 sm:px-10 md:px-14 lg:px-16
        pt-7 sm:pt-10 md:pt-14
        pb-8 sm:pb-12 md:pb-14
        overflow-hidden
        shadow-[0_30px_80px_-20px_rgba(0,0,0,0.5)]
        transition-[opacity,transform,border-color] duration-300 ease-out
        ${isActive
          ? "opacity-100 scale-100 border-white/[0.14]"
          : "opacity-[0.85] scale-[0.94]"}
      `}
    >
      {/* Soft accent rule at top of each card — gives the peek an unmistakable
          identity vs the dark section background. Editorial, not chrome. */}
      <div
        aria-hidden
        className="absolute top-0 left-8 right-8 h-px"
        style={{
          background: `linear-gradient(90deg, transparent 0%, ${project.accent}55 50%, transparent 100%)`,
        }}
      />
      {/* Card-internal layout: 2 columns on md+, stacked on mobile */}
      <div className="grid md:grid-cols-[1fr_auto] gap-8 md:gap-12 lg:gap-16 items-center">
        {/* LEFT: meta + copy + actions */}
        <div className="order-2 md:order-1 min-w-0">
          <div className="flex items-center gap-3">
            <span className="text-[10.5px] tracking-[0.24em] uppercase text-zinc-400">
              {project.category}
            </span>
            <span className="block w-1 h-1 rounded-full bg-zinc-700" />
            <span
              data-testid={`portfolio-status-${project.id}`}
              className="inline-flex items-center gap-1.5 text-[10.5px] tracking-[0.18em] uppercase text-zinc-400"
            >
              <span
                className="block w-1.5 h-1.5 rounded-full pulse-dot"
                style={{ background: project.accent }}
              />
              {project.status || "Live"}
            </span>
          </div>

          <h3 className="mt-4 font-display text-3xl sm:text-4xl md:text-5xl tracking-tight text-white leading-[1.05]">
            {project.name}
          </h3>

          <a
            href={project.liveUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`portfolio-domain-${project.id}`}
            className="mt-3 inline-flex items-center gap-1.5 text-[12.5px] text-zinc-400 hover:text-zinc-200 transition-colors"
          >
            <Globe size={12} weight="regular" />
            <span className="truncate">{project.domain}</span>
            <ArrowSquareOut size={11} weight="bold" />
          </a>

          <div className="mt-6 sm:mt-7 grid gap-4 max-w-[46ch]">
            <div>
              <div className="text-[10.5px] tracking-[0.22em] uppercase text-zinc-500">The need</div>
              <p className="mt-1.5 text-[14px] sm:text-[14.5px] text-zinc-400 leading-relaxed">{project.need}</p>
            </div>
            <div>
              <div className="text-[10.5px] tracking-[0.22em] uppercase text-zinc-500">What we delivered</div>
              <p data-testid={`portfolio-delivered-${project.id}`} className="mt-1.5 text-[14px] sm:text-[14.5px] text-zinc-200 leading-relaxed">{project.delivered}</p>
            </div>
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={onPreview}
              data-testid={`portfolio-live-${project.id}`}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-black text-[13px] font-medium hover:bg-zinc-200 transition-colors"
            >
              Live Preview
              <ArrowUpRight size={13} weight="bold" />
            </button>
            <a
              href={project.liveUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`portfolio-open-${project.id}`}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/[0.12] text-white text-[13px] hover:bg-brand-700/25 hover:border-brand-500/50 transition-colors"
            >
              Open Site
              <ArrowSquareOut size={13} weight="bold" />
            </a>
            <button
              type="button"
              onClick={() => shareSite(project)}
              data-testid={`portfolio-share-${project.id}`}
              aria-label={`Share ${project.name} with a friend`}
              title="Send this site to a friend"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-white/[0.12] text-zinc-300 text-[13px] hover:text-white hover:bg-brand-700/25 hover:border-brand-500/50 transition-colors"
            >
              <ShareNetwork size={14} weight="bold" />
              Share
            </button>
          </div>
        </div>

        {/* RIGHT: hero phone */}
        <button
          type="button"
          onClick={onPreview}
          aria-label={`Open live preview of ${project.name}`}
          data-testid={`portfolio-preview-${project.id}`}
          className="order-1 md:order-2 relative flex justify-center cursor-pointer self-center"
        >
          <div
            className="
              phone-mockup
              w-[240px] sm:w-[280px] md:w-[300px] lg:w-[330px] xl:w-[360px]
              transition-transform duration-700 ease-out
              hover:-translate-y-1.5
            "
            style={{
              boxShadow:
                "0 50px 120px -25px rgba(0,0,0,0.85), 0 25px 60px -20px rgba(0,0,0,0.55)",
            }}
          >
            <div className="notch" />
            <div className="screen bg-black">
              <img
                src={project.image}
                alt={`${project.name} — ${project.category.toLowerCase()} website built by WebJesters, mobile view`}
                width="780"
                height="1356"
                loading={index === 0 ? "eager" : "lazy"}
                decoding="async"
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          <AnimatePresence>
            {hover && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
                className="absolute inset-0 flex items-center justify-center pointer-events-none"
              >
                <span className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white text-black text-[12.5px] font-medium shadow-[0_18px_40px_-10px_rgba(0,0,0,0.6)]">
                  <Cursor size={13} weight="fill" />
                  Tap to load live site
                </span>
              </motion.div>
            )}
          </AnimatePresence>
        </button>
      </div>
    </motion.div>
  );
}

function LivePreviewModal({ project, onClose }) {
  const closeBtnRef = useRef(null);

  useEffect(() => {
    if (!project) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    const focusTimer = setTimeout(() => {
      closeBtnRef.current?.focus();
    }, 50);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      clearTimeout(focusTimer);
    };
  }, [project, onClose]);

  return (
    <AnimatePresence>
      {project && (
        <motion.div
          data-testid="live-preview-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          onClick={onClose}
          className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-md flex items-center justify-center p-4 sm:p-8"
        >
          <motion.div
            initial={{ y: 30, opacity: 0, scale: 0.97 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 20, opacity: 0, scale: 0.97 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-5xl max-h-[92vh] rounded-3xl bg-[#0e0e10] border border-white/10 overflow-hidden shadow-[0_40px_120px_-20px_rgba(0,0,0,0.8)]"
          >
            <div className="flex items-center justify-between px-4 sm:px-5 h-12 border-b border-white/[0.07] bg-[#141417]">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-[#ff5f57]" />
                <span className="w-3 h-3 rounded-full bg-[#febc2e]" />
                <span className="w-3 h-3 rounded-full bg-[#28c840]" />
              </div>
              <div className="hidden sm:flex items-center gap-2 px-3 py-1 rounded-md bg-[#0a0a0c] border border-white/[0.06] text-[11.5px] text-zinc-400 max-w-[55%] truncate">
                <Globe size={11} weight="regular" />
                {project.liveUrl}
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => shareSite(project)}
                  data-testid="live-preview-share"
                  aria-label="Share this site"
                  className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-white/10 bg-white/[0.03] text-white hover:bg-brand-700/25 hover:border-brand-500/50 transition-colors"
                >
                  <ShareNetwork size={14} weight="bold" />
                </button>
                <a
                  href={project.liveUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="live-preview-open"
                  className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white text-black text-[11.5px] font-medium hover:bg-zinc-200 transition-colors"
                >
                  Open site
                  <ArrowSquareOut size={11} weight="bold" />
                </a>
                <button
                  type="button"
                  ref={closeBtnRef}
                  onClick={onClose}
                  data-testid="live-preview-close"
                  aria-label="Close preview"
                  className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-white/10 bg-white/[0.03] text-white hover:bg-brand-700/25 hover:border-brand-500/50 transition-colors"
                >
                  <X size={14} weight="bold" />
                </button>
              </div>
            </div>

            <div className="relative bg-[#0a0a0c] h-[78vh] sm:h-[80vh]">
              <iframe
                title={`${project.name} live preview`}
                src={project.liveUrl}
                loading="eager"
                referrerPolicy="no-referrer"
                sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                className="w-full h-full bg-white"
              />
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
